import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { useRouter } from "next/router";
import { RegisterOptions, useFormContext } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import ChooseImage, {
  ChooseImageProps,
} from "components/form/chooseImage/ChooseImage";
import RadioGroup, {
  FreeEntryProps,
} from "components/form/radioGroup/RadioGroup";
import { RadioGroupFieldSlice } from "prismicio-types";
import { useGetProduct } from "queries/product-queries";
import {
  FieldSliceContext,
  RadioGroupSchemaField,
  ChooseImageSchemaField,
} from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  getInputPropsFromSchemaRules,
  isFieldType,
  useFindByKey,
  useGetUpsellForField,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function RadioGroupField({
  slice,
}: Readonly<SliceComponentProps<RadioGroupFieldSlice, FieldSliceContext>>) {
  const {
    formState: { errors },
  } = useFormContext();

  const { data: product } = useGetProduct();
  const router = useRouter();

  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const [fieldKey, groupedFieldKey] = schemaKey.split(",");
  const field = findByKey(fieldKey);
  const groupedField = groupedFieldKey ? findByKey(groupedFieldKey) : null;
  const upsells = useGetUpsellForField(field?.urlKey);

  if (!field) {
    return null;
  }

  if (
    !isFieldType<RadioGroupSchemaField>(field, "radio_group") ||
    (groupedField &&
      !isFieldType<ChooseImageSchemaField>(groupedField, "choose_image"))
  ) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "radio_group",
      field.metas[0].canvasComponent
    );
  }

  const { maxLength, minLength } = getInputPropsFromSchemaRules(field.rules);

  // Get validation options to spread into the `react-hook-form` `register` call
  const registerOptions: RegisterOptions = {
    ...(maxLength && {
      maxLength: {
        value: maxLength,
        message: interpolate(
          product.content.canvas.form_field_max_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_max_length_error_message
        ),
      },
    }),
    ...(minLength && {
      minLength: {
        value: minLength,
        message: interpolate(
          product.content.canvas.form_field_min_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_min_length_error_message
        ),
      },
    }),
    required:
      field.flags?.presence === "required" &&
      interpolate(
        product.content.canvas.form_field_required_error_message ||
          CANVAS_FALLBACK_CONTENT.form_field_required_error_message
      ),
  };

  // Show a warning message if there are upsells when editing an order
  const isDisabled = !!(upsells && router.query.order_id);
  const groupedFieldError =
    groupedField && _get(errors, groupedField?.urlKey)?.message;
  const error =
    errors && (_get(errors, field.urlKey)?.message || groupedFieldError);

  let freeEntryProps: FreeEntryProps | undefined,
    chooseImageProps: ChooseImageProps | undefined;

  if (slice.variation === "withFreeEntry") {
    freeEntryProps = {
      component: field.metas[0].freeEntryComponent,
      label: interpolate(slice.primary.free_entry_label || ""),
      maxLength,
      placeholder: interpolate(slice.primary.free_entry_placeholder || ""),
    };
  }

  if (
    slice.variation === "asCardsWithChooseImage" &&
    groupedField?.metas[0].canvasComponent === "choose_image"
  ) {
    chooseImageProps = {
      aspectRatio: groupedField?.metas[0].aspectRatio,
      mobileColumns: slice.primary.choose_image_columns ?? undefined,
      desktopColumns: slice.primary.choose_image_desktop_columns ?? undefined,
      name: groupedField?.urlKey,
      options: groupedField?.metas[0].images,
      registerOptions,
    };
  }

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label className={styles.label} htmlFor={field.urlKey}>
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <RadioGroup
          freeEntryProps={freeEntryProps}
          name={field.urlKey}
          options={field.metas[0].options || field.allow}
          registerOptions={registerOptions}
          variation={slice.variation}
          upsells={upsells}
          {...(slice.variation === "asCards" ||
          slice.variation === "asCardsWithChooseImage"
            ? {
                layout: "cards",
                mobileColumns: slice.primary.columns ?? undefined,
                desktopColumns: slice.primary.desktop_columns ?? undefined,
              }
            : {
                layout: "inline",
              })}
        />

        {chooseImageProps && (
          <div className={styles.imagesGroup}>
            <ChooseImage {...chooseImageProps} />
          </div>
        )}
      </ErrorBoundary>

      {isDisabled && (
        <p className={styles.warning}>
          {interpolate(
            product.content.canvas.form_field_disabled_message ||
              CANVAS_FALLBACK_CONTENT.form_field_disabled_message
          )}
        </p>
      )}

      {error && <p className={styles.error}>{error as string}</p>}
    </div>
  );
}
